<template>
  <div class="ocr-file-panel" :class="`ocr-file-panel--${bucket}`">
    <div class="ocr-file-panel-header">
      <div class="ocr-file-panel-heading">
        <h3 class="ocr-file-panel-title">{{ title }}</h3>
        <p v-if="subtitle" class="ocr-file-panel-subtitle">{{ subtitle }}</p>
      </div>
      <div class="ocr-file-panel-meta">
        <span v-if="!loading" class="ocr-file-count-chip">{{ filteredFiles.length }}</span>
        <v-btn
          icon
          variant="text"
          size="small"
          class="ocr-file-refresh-btn"
          :loading="loading"
          aria-label="Refresh file list"
          @click="load"
        >
          <v-icon size="20">mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="ocr-file-list">
      <div v-for="n in 4" :key="n" class="ocr-file-row ocr-file-row--skeleton">
        <OcrSkLine width="40px" height="40px" round />
        <div class="ocr-file-row-text">
          <OcrSkLine width="72%" height="14px" />
          <OcrSkLine width="40%" height="11px" />
        </div>
      </div>
    </div>

    <template v-else>
      <v-text-field
        v-if="files.length > 4"
        v-model="search"
        class="ocr-file-search"
        placeholder="Search files…"
        variant="outlined"
        density="compact"
        hide-details
        prepend-inner-icon="mdi-magnify"
        clearable
      />

      <div v-if="!filteredFiles.length" class="ocr-file-empty">
        <div class="ocr-file-empty-icon" aria-hidden="true">
          <v-icon :icon="emptyIcon" size="36" />
        </div>
        <p class="ocr-file-empty-title">{{ emptyTitle }}</p>
        <p class="ocr-file-empty-hint">{{ emptyHint }}</p>
      </div>

      <ul v-else class="ocr-file-list" role="list">
        <li
          v-for="(item, index) in filteredFiles"
          :key="item.name"
          class="ocr-file-row"
          :class="{
            'ocr-file-row--selected': selectable && isSelected(item.name),
            'ocr-file-row--active': activeName === item.name,
          }"
          :style="{ '--ocr-file-delay': `${Math.min(index, 8) * 40}ms` }"
          @click="selectable && toggleRow(item.name)"
        >
          <v-checkbox
            v-if="selectable"
            class="ocr-file-row-check"
            :model-value="isSelected(item.name)"
            density="compact"
            hide-details
            @click.stop
            @update:model-value="setSelected(item.name, $event)"
          />
          <div class="ocr-file-icon-wrap" :class="`ocr-file-icon-wrap--${item.kind}`">
            <v-icon :icon="item.icon" size="22" />
          </div>
          <div class="ocr-file-row-text">
            <span class="ocr-file-name" :title="item.name">{{ item.displayName }}</span>
            <span class="ocr-file-meta">
              <span class="ocr-file-ext">{{ item.ext || "file" }}</span>
              <span class="ocr-file-dot" aria-hidden="true">·</span>
              <span class="ocr-file-status-label">{{ statusLabel }}</span>
            </span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import ocrApi from "@/services/ocrApi";
import OcrSkLine from "@/components/layout/OCR/OcrSkLine.vue";

const BUCKET_COPY = {
  uploaded: {
    subtitle: "Waiting in the upload folder for processing",
    emptyTitle: "No uploads yet",
    emptyHint: "Choose PDF or images above, then click Upload.",
    emptyIcon: "mdi-cloud-upload-outline",
    statusLabel: "Queued",
  },
  processing: {
    subtitle: "Currently in the OCR processing queue",
    emptyTitle: "Queue is empty",
    emptyHint: "Select uploaded files and click Process selected.",
    emptyIcon: "mdi-progress-clock",
    statusLabel: "Processing",
  },
  completed: {
    subtitle: "Finished and ready in the completed folder",
    emptyTitle: "Nothing completed yet",
    emptyHint: "Processed files will appear here.",
    emptyIcon: "mdi-check-circle-outline",
    statusLabel: "Completed",
  },
};

function fileKind(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "tif", "tiff"].includes(ext)) return "image";
  if (["xls", "xlsx", "csv"].includes(ext)) return "sheet";
  return "doc";
}

function fileIcon(kind) {
  const map = {
    pdf: "mdi-file-pdf-box",
    image: "mdi-file-image-outline",
    sheet: "mdi-file-excel-outline",
    doc: "mdi-file-document-outline",
  };
  return map[kind] || map.doc;
}

export default {
  name: "OcrFileList",
  components: { OcrSkLine },
  props: {
    title: { type: String, required: true },
    bucket: { type: String, required: true },
    refreshKey: { type: Number, default: 0 },
    selectable: { type: Boolean, default: false },
    selected: { type: Array, default: () => [] },
    extensions: { type: String, default: "" },
    names: { type: Array, default: null },
    activeName: { type: String, default: "" },
  },
  emits: ["update:selected"],
  data() {
    return { loading: false, files: [], search: "" };
  },
  computed: {
    copy() {
      return BUCKET_COPY[this.bucket] || BUCKET_COPY.uploaded;
    },
    subtitle() {
      return this.copy.subtitle;
    },
    emptyTitle() {
      return this.search ? "No matching files" : this.copy.emptyTitle;
    },
    emptyHint() {
      return this.search ? "Try a different search term." : this.copy.emptyHint;
    },
    emptyIcon() {
      return this.search ? "mdi-file-search-outline" : this.copy.emptyIcon;
    },
    statusLabel() {
      return this.copy.statusLabel;
    },
    fileItems() {
      return (this.files || []).map((name) => {
        const kind = fileKind(name);
        const parts = name.split(".");
        const ext = parts.length > 1 ? parts.pop().toUpperCase() : "";
        return {
          name,
          displayName: name,
          ext,
          kind,
          icon: fileIcon(kind),
        };
      });
    },
    filteredFiles() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.fileItems;
      return this.fileItems.filter((f) => f.name.toLowerCase().includes(q));
    },
  },
  watch: {
    refreshKey: { immediate: true, handler() { this.load(); } },
    names: { handler() { this.load(); } },
    bucket() {
      this.search = "";
      this.load();
    },
  },
  methods: {
    isSelected(name) {
      return (this.selected || []).includes(name);
    },
    setSelected(name, checked) {
      const set = new Set(this.selected || []);
      if (checked) set.add(name);
      else set.delete(name);
      this.$emit("update:selected", [...set]);
    },
    toggleRow(name) {
      this.setSelected(name, !this.isSelected(name));
    },
    async load() {
      if (this.names) {
        this.files = [...this.names];
        this.loading = false;
        return;
      }
      const quiet = this.files.length > 0;
      if (!quiet) this.loading = true;
      try {
        const params = { bucket: this.bucket };
        if (this.extensions?.trim()) params.extensions = this.extensions.trim();
        const { data } = await ocrApi.get("api/v1/files", { params });
        this.files = data.files || [];
        if (this.selectable && this.selected?.length) {
          const names = new Set(this.files);
          this.$emit(
            "update:selected",
            this.selected.filter((n) => names.has(n)),
          );
        }
      } catch {
        this.files = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
